import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { logError } from 'components/LogError';
import { CreatePaymentSessionMutation, PaymentSessionInput } from '__generated__/graphql';
import { CREATE_PAYMENT_SESSION } from '../graphql/createPaymentSession';

/**
 * Shape the checkout.com Flow web SDK (`loadCheckoutWebComponents`) expects for its `paymentSession`.
 * Web Flow consumes the base64 `payment_session_token` — NOT the `pss_...` `payment_session_secret`
 * the mobile SDK uses.
 */
interface FlowPaymentSession {
  id: string;
  payment_session_token: string;
}

interface ReturnProps {
  createSession: (input: PaymentSessionInput) => Promise<FlowPaymentSession | null>;
  loading: boolean;
}

/**
 * checkout.com Flow migration. Creates the backend payment session that the Flow SDK initialises with
 * to render the card fields. The session is only used to render/tokenize the card; the actual charge
 * still goes through `createCardPayment` (see `useCreateCheckoutDotComPayment`).
 */
export function useCreateCheckoutDotComPaymentSession(): ReturnProps {
  const [postCreatePaymentSession, { loading }] = useMutation<CreatePaymentSessionMutation>(CREATE_PAYMENT_SESSION, {
    onError: (error) => logError(error, { tags: { userFlow: 'payment' } }),
  });

  const createSession = useCallback(
    async (input: PaymentSessionInput): Promise<FlowPaymentSession | null> => {
      try {
        const { data } = await postCreatePaymentSession({ variables: { paymentSession: input } });
        const session = data?.createPaymentSession;

        if (session?.id && session?.paymentSessionToken) {
          return { id: session.id, payment_session_token: session.paymentSessionToken };
        }

        logError(new Error(session?.errorMessage || 'createPaymentSession returned no session'), {
          tags: { userFlow: 'payment' },
        });
        return null;
      } catch (error) {
        logError(error as Error, { tags: { userFlow: 'payment' } });
        return null;
      }
    },
    [postCreatePaymentSession],
  );

  return { createSession, loading };
}
